/*
 * QQQ - Low-code Application Framework for Engineers.
 * Copyright (C) 2021-2026.  Kingsrook, LLC
 * 651 N Broad St Ste 205 # 6917 | Middletown DE 19709 | United States
 * contact@kingsrook.com
 * https://github.com/Kingsrook/
 *
 * This program is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Affero General Public License as
 * published by the Free Software Foundation, either version 3 of the
 * License, or (at your option) any later version.
 *
 * This program is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
 * GNU Affero General Public License for more details.
 *
 * You should have received a copy of the GNU Affero General Public License
 * along with this program.  If not, see <https://www.gnu.org/licenses/>.
 */

import {QJobComplete} from "@temple-of-syrinx/qqq-frontend-core/lib/model/processes/QJobComplete";
import {QJobError} from "@temple-of-syrinx/qqq-frontend-core/lib/model/processes/QJobError";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import ButtonBase from "@mui/material/ButtonBase";
import CircularProgress from "@mui/material/CircularProgress";
import Icon from "@mui/material/Icon";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import colors from "qqq/assets/theme/base/colors";
import MDTypography from "qqq/components/legacy/MDTypography";
import {detectBasePath} from "qqq/utils/PathUtils";
import Client from "qqq/utils/qqq/Client";
import {generateMenuItemId} from "qqq/utils/qqqIdUtils";
import {useState} from "react";

const qController = Client.getInstance();

export interface ScopeSelectorOption
{
   //////////////////////////////////////////////////////////////////////
   // string as well as number, because an application's scopes may be //
   // keyed by slug or uuid rather than by an integer id.              //
   //////////////////////////////////////////////////////////////////////
   id: number | string;
   label: string;
   isActive: boolean;
}

export interface ScopeSelectorPayload
{
   ////////////////////////////////////////////////////////////////////////////
   // optional caption rendered above the control.  Omit it from the payload //
   // to render the control with no caption at all.                          //
   ////////////////////////////////////////////////////////////////////////////
   label?: string;
   iconName: string;
   processName: string;

   ///////////////////////////////////////////////////////////////////////////
   // the name of the input the switch process reads the chosen option's id //
   // from.  Carried in the payload like everything else app-specific - an  //
   // application whose process takes tenantId or warehouseId names it here //
   // rather than being forced to call its input clientId.                  //
   ///////////////////////////////////////////////////////////////////////////
   fieldName: string;
   options: ScopeSelectorOption[];
}

interface Props
{
   payload?: ScopeSelectorPayload;

   ////////////////////////////////////////////////////////////////////////
   // computed by SideNav from the same theme state this control renders //
   // against, and passed in rather than recomputed, so a future theme   //
   // branch cannot leave this one control illegible.                    //
   ////////////////////////////////////////////////////////////////////////
   textColor?: "white" | "dark" | "inherit";

   ////////////////////////////////////////////////////////////////////////////
   // when the drawer is collapsed to icon width, the label and chevron      //
   // collapse with it - as every other sidenav element's text does - rather //
   // than overflowing the drawer or taking the whole control away.          //
   ////////////////////////////////////////////////////////////////////////////
   miniSidenav?: boolean;
}

/*******************************************************************************
 ** Tenant-style switcher rendered in the left nav, just below the user's
 ** profile item.
 **
 ** Deliberately NOT built out of SideNavItem, and not rendered as a route -
 ** children of a SideNavCollapse read as navigation links, and this needs to
 ** read as a scope control instead.
 **
 ** The payload is self-describing (it carries its own label, icon, and
 ** process name) so this component stays generic - it must never hardcode
 ** the word "client".
 **
 ** Nothing is rendered unless the payload carries at least two options and
 ** the name of the field to send the chosen one under.  A single-scope user
 ** has nothing to switch among, and an application that sends one option (or
 ** none) gets no control rather than a dropdown that cannot change anything.
 *******************************************************************************/
export default function SideNavScopeSelector({payload, textColor = "white", miniSidenav = false}: Props): JSX.Element
{
   const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
   const [busy, setBusy] = useState(false);
   const [error, setError] = useState<string | null>(null);

   if (!payload || !payload.options || payload.options.length < 2)
   {
      return (null);
   }

   if (!payload.fieldName)
   {
      ///////////////////////////////////////////////////////////////////////////
      // without it there is nothing to name the chosen id when calling the    //
      // process - it would arrive as an input called "undefined", and the     //
      // process would switch to nothing while this reloaded as though it had. //
      ///////////////////////////////////////////////////////////////////////////
      console.warn("sessionScopeSelector payload has no fieldName; not rendering the scope selector.");
      return (null);
   }

   ///////////////////////////////////////////////////////////////////////////////
   // a payload with no option marked active is malformed.  Fall back to the    //
   // first one, but treat that fallback AS active for everything below - so it //
   // carries the checkmark and clicking it is the same no-op as clicking a     //
   // genuinely active option.  Rendering nothing instead would leave a person  //
   // with several scopes no way at all to change scope.                        //
   ///////////////////////////////////////////////////////////////////////////////
   const activeFromPayload = payload.options.find((option) => option.isActive);
   if (!activeFromPayload)
   {
      console.warn("sessionScopeSelector payload has no option marked isActive; showing the first option as current.");
   }
   const active = activeFromPayload ?? payload.options[0];

   /***************************************************************************
    **
    ***************************************************************************/
   const selectOption = async (option: ScopeSelectorOption) =>
   {
      ////////////////////////////////////////////////////////////////////
      // clear ahead of the no-op return, and on menu close, so a prior //
      // failure's message cannot outlive the thing it was about.       //
      ////////////////////////////////////////////////////////////////////
      setError(null);
      setAnchorEl(null);
      if (option === active)
      {
         return;
      }

      setBusy(true);

      try
      {
         const formData = new FormData();
         formData.append(payload.fieldName, String(option.id));
         const processResult = await qController.processRun(payload.processName, formData, null, true);

         ///////////////////////////////////////////////////////////////////////////////
         // Only a COMPLETED job is a successful switch.  dontGoAsyncOnBackend raises //
         // the step timeout, it does not prevent going async - past that timeout the //
         // backend returns a QJobStarted, and treating that as success would clear   //
         // the session cache and navigate while the backend is still on the old      //
         // scope, showing a successful reload for a switch that had not happened     //
         // (and swallowing the error if the job then threw).  This control has       //
         // nowhere to poll from, so anything other than complete is a failure here.  //
         ///////////////////////////////////////////////////////////////////////////////
         if (!(processResult instanceof QJobComplete))
         {
            setBusy(false);
            setError(processResult instanceof QJobError
               ? (processResult.userFacingError ?? processResult.error)
               : "Could not switch - the request did not complete.  Please try again.");
            return;
         }

         /////////////////////////////////////////////////////////////////////////////
         // MANDATORY, not hygiene.  Session values are cached in localStorage, and //
         // the auth module only refreshes that cache when the access token         //
         // rotates or the analytics identity is missing - so without this          //
         // removal, the reload below re-reads the stale cache and keeps marking    //
         // the OLD scope active, and the switch silently appears not to work.      //
         // Removing the key drops the reload into the existing                     //
         // refresh-from-cookie path in the auth module.                            //
         /////////////////////////////////////////////////////////////////////////////
         localStorage.removeItem("sessionValues");

         ////////////////////////////////////////////////////////////////////////////
         // a full page navigation (not react-router) is required here - it clears //
         // prior-scope data off the screen, lands on the new scope's default      //
         // surface, and lets the backend's own root-redirect route to the correct //
         // landing page for that scope's type.                                    //
         ////////////////////////////////////////////////////////////////////////////
         //////////////////////////////////////////////////////////////////////////
         // through the detected base path, since the app may be served from a   //
         // prefix - a bare "/" would land on the server's web root, which reads //
         // as having been logged out mid-switch.                                //
         //////////////////////////////////////////////////////////////////////////
         const basePath = detectBasePath();
         window.location.href = basePath && basePath !== "/" ? `${basePath}/` : "/";
      }
      catch (e: any)
      {
         setBusy(false);
         setError(e?.message ?? "Could not switch.");
      }
   };

   return (
      <Box px={2} pb={1} data-qqq-id="sidenav-scope-selector">
         {payload.label && !miniSidenav && (
            <MDTypography data-qqq-id="sidenav-scope-selector-label" variant="caption" color={textColor} sx={{fontSize: "1rem", fontWeight: 400}}>
               {payload.label}
            </MDTypography>
         )}
         <ButtonBase
            data-qqq-id="sidenav-scope-selector-trigger"
            disabled={busy}
            aria-haspopup="true"
            aria-expanded={Boolean(anchorEl)}
            aria-label={payload.label ? `${payload.label} ${active.label}` : active.label}
            onClick={(event: any) => setAnchorEl(event.currentTarget)}
            sx={{
               display: "flex",
               width: "100%",
               alignItems: "center",
               justifyContent: "flex-start",
               gap: 1,
               border: "1px solid",
               borderColor: colors.grayLines.main,
               borderRadius: "0.5rem",
               px: 1,
               py: 0.75,
               cursor: busy ? "default" : "pointer",
            }}
         >
            <Icon sx={{fontSize: "24px !important", ml: "0.375rem", mr: miniSidenav ? 0 : "0.5rem"}}>{payload.iconName}</Icon>

            {/*
               In mini mode the label and chevron collapse rather than the whole control
               disappearing - the same shape SideNavCollapse/SideNavItem use, and the
               reason matters: SideNav auto-minis below 1200px, so removing the control
               would leave a multi-scope person on a narrow window with nothing to click.
            */}
            <MDTypography
               variant="button"
               color={textColor}
               noWrap
               sx={{
                  flexGrow: 1,
                  fontSize: "1rem",
                  fontWeight: 400,
                  textAlign: "left",
                  opacity: miniSidenav ? 0 : 1,
                  maxWidth: miniSidenav ? 0 : "100%",
                  transition: "opacity 200ms, max-width 200ms",
               }}
            >
               {active.label}
            </MDTypography>
            {!miniSidenav && (busy ? <CircularProgress size={16} /> : <Icon fontSize="small" sx={{position: "relative", left: "-0.125rem"}}>expand_more</Icon>)}
         </ButtonBase>
         {error && (
            <Alert data-qqq-id="sidenav-scope-selector-error" severity="error" onClose={() => setError(null)} sx={{mt: 1, fontSize: "0.875rem"}}>
               {error}
            </Alert>
         )}
         <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={() => setAnchorEl(null)}>
            {/* MUI keeps the menu mounted, and this control re-renders on every sidenav hover - so only build the items while it is open. */}
            {Boolean(anchorEl) && payload.options.map((option) => (
               <MenuItem
                  key={option.id}
                  data-qqq-id={generateMenuItemId(`scope-${option.id}`)}
                  onClick={() => selectOption(option)}
                  selected={option === active}
               >
                  <Icon fontSize="small" sx={{mr: 1, visibility: option === active ? "visible" : "hidden"}}>
                     check
                  </Icon>
                  {option.label}
               </MenuItem>
            ))}
         </Menu>
      </Box>
   );
}
